import ToolTip from 'core/components/Tooltip';
import React, {
  useRef,
} from 'react';
import InfoRow from 'views/EMR/components/Insurance/InsuranceCard/InfoRow';
import Divider from '@mui/material/Divider';
import {
  InsuranceDTO,
} from 'dtos/insuranceDTO';
import {
  PatientDTO,
} from 'dtos/patientDTO';
import {
  useInsuranceMetaData,
} from 'views/EMR/components/Insurance/hooks';
import isNil from 'lodash/isNil';
import {
  formatMoney,
  formatPhone,

  isEmptyString,
} from '@populateui/utils';
import {
  getAddressString,
} from 'utils/string';
import {
  getDisplayName,
} from 'pages/Dashboard/utils/helper';
import keyBy from 'lodash/keyBy';
import {
  formatDate,
  formatTwoDigitDate,
} from 'utils/date';
import InsuranceEligibility from 'pages/Dashboard/components/InsuranceEligibility';
import {
  Demographics,
} from 'views/EMR/components/Insurance/Dialog/InsuranceForm';

type Props = InsuranceDTO & {
  demographics?: Demographics;
  patient: PatientDTO;
  updatingInsurances?: boolean;
};

export default function InsuranceInfo({
  demographics,
  patient,
  updatingInsurances,
  ...insurance
}: Props) {
  const {
    insuranceOwnerRelationshipType,
    employerName,
    mspType,
    memberNumber,
    copay,
    groupName,
    groupNumber,
    policyNumber,
    effectiveFrom,
    effectiveTill,
    insuranceOwnerAddress,
    insuranceOwnerPhoneNumber,
    notes,
  } = insurance;
  const { relationshipTypesByName, mspTypes } = useInsuranceMetaData();
  const keyedMSPTypes = keyBy(mspTypes ?? [], 'code');
  const {
    isEmployerInsurance,
  } = relationshipTypesByName[insuranceOwnerRelationshipType ?? ''] ?? {};

  const notesRef = useRef<HTMLElement>(null);

  return (
    <div className="flex flex-col gap-3 px-4 pb-4 flex-1">
      <div className="flex flex-col items-start text-xs">
        <div className="text-xs font-semibold">Effective</div>
        <div className="flex items-center justify-between w-full flex-wrap gap-2">
          <div className="flex gap-2 items-center">

            <div className="flex gap-1">
              <span className="font-medium text-gray-600">From:</span>
              {isNil(effectiveFrom) ? (
                <span className="font-medium text-gray-900">—</span>
              ) : (
                <span className="font-medium text-gray-900">
                  {formatTwoDigitDate(effectiveFrom ?? '')}
                </span>
              )}
            </div>
            <div className="flex gap-1">
              <span className="font-medium text-gray-600">To:</span>
              {isNil(effectiveTill) ? (
                <span className="font-medium text-gray-900">—</span>
              ) : (
                <span className="font-medium text-gray-900">
                  {formatTwoDigitDate(effectiveTill ?? '')}
                </span>
              )}
            </div>
          </div>
          <InsuranceEligibility
            insurance={insurance ?? {}}
            patient={patient}
            disabled={updatingInsurances}
            classes={{
              root: '!flex-row-reverse !space-x-0 gap-2',
              lastChecked: '!flex-row-reverse !gap-1 !space-x-0',
            }}
          />
        </div>
      </div>
      <Divider className="!border-gray-100" />
      {/* Insurance Info */}
      <div className="flex justify-between flex-wrap gap-x-8 gap-y-1">
        {/* Left Column */}
        <div className="flex flex-col gap-1 flex-1">
          <InfoRow label="Member ID" value={memberNumber} />
          <InfoRow label="Copay" value={formatMoney(copay ?? 0)} />
          <InfoRow label="Relationship to Subscriber" value={relationshipTypesByName[insuranceOwnerRelationshipType ?? '']?.name ?? '--'} />
          {isEmployerInsurance && (
            <InfoRow label="Employer" value={employerName} />
          )}
          {!isNil(mspType) && (
            <InfoRow label="MSP Type" value={keyedMSPTypes?.[mspType ?? '']?.name} />
          )}
        </div>

        {/* Right Column */}
        <div className="flex flex-col gap-1 flex-1">
          {!isEmptyString(groupName) && (
            <InfoRow label="Group Name" value={groupName} />
          )}
          {!isEmptyString(groupNumber) && (
            <InfoRow label="Group Number" value={groupNumber} />
          )}
          {!isEmptyString(policyNumber) && (
            <InfoRow label="Policy Number" value={policyNumber} />
          )}
        </div>
      </div>

      {!isEmptyString(notes) && (
        <>
          <Divider className="!border-gray-100" />
          <div className="flex flex-col items-start text-xs">
            <div className="text-sm font-semibold">Notes</div>
            <ToolTip
              title={notes}
              auto
              classes={{ tooltip: 'w-[250px] whitespace-pre-wrap break-words' }}
              ref={notesRef}
            >
              <span className="line-clamp-2 text-gray-600 font-medium" ref={notesRef}>{notes}</span>
            </ToolTip>
          </div>
        </>
      )}
      <Divider className="!border-gray-100" />
      <div>
        <div className="text-xs font-semibold">Subscriber</div>
        <div className="text-xs text-gray-600 font-medium flex gap-1">
          <span className="font-semibold">
            {getDisplayName(demographics?.formattedNames, '--')}
            {` (${demographics?.genderDescription})`}
          </span>
          born on
          <span className="font-semibold">
            {formatDate(demographics?.dateOfBirth ?? '')}
          </span>
        </div>
        {!isEmptyString(insuranceOwnerAddress) && (
          <div className="text-xs text-gray-600 font-medium">
            {getAddressString(insuranceOwnerAddress)}
          </div>
        )}
        {!isEmptyString(insuranceOwnerPhoneNumber) && (
          <div className="text-xs text-gray-600 font-medium">
            {formatPhone(insuranceOwnerPhoneNumber ?? '')}
          </div>
        )}
      </div>
    </div>
  );
}
